import moment from "moment-timezone";
import { call, cancelled, put, select, takeLatest } from "redux-saga/effects";
import { PLOT } from "../constants";
import { setPlotData, setPlotError, setPlotLoading, setPlotSummary } from "../actions";

const TIMEZONE = "Asia/Tokyo";
const DEFAULT_FETCH_DAYS = 30;

const S3_BUCKET_BASE_URL = (process.env.REACT_APP_S3_BUCKET_BASE_URL || "").replace(/\/+$/, "");
const PLOT_CLOUDFRONT_BASE_URL = (process.env.REACT_APP_PLOT_CLOUDFRONT_BASE_URL || process.env.REACT_APP_PICTREE_DATA_BASE_URL || "").replace(
  /\/+$/,
  ""
);
const PLOT_CLOUDFRONT_DISTRIBUTION_ID = process.env.REACT_APP_PLOT_CLOUDFRONT_DISTRIBUTION_ID || "";

const dailyArtifactCache = new Map();

const emptySummary = () => ({
  totals: {
    plots: 0,
    photos: 0,
    scannedRows: 0,
    skippedRows: 0,
  },
  trustCounts: [],
  eventCounts: [],
  municipalityCounts: [],
  assetTypeCounts: [],
});

const normalizeBaseUrl = (url) => String(url || "").replace(/\/+$/, "");

const normalizeRange = (dateRange) => {
  if (Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
    const startDate = moment(dateRange[0]).tz(TIMEZONE);
    const endDate = moment(dateRange[1]).tz(TIMEZONE);
    const today = moment().tz(TIMEZONE).startOf("day");
    const availableEndDate = endDate.isSame(today, "day") ? today.clone().subtract(1, "day") : endDate;

    return [
      startDate.format("YYYY-MM-DD"),
      availableEndDate.format("YYYY-MM-DD"),
    ];
  }

  const today = moment().tz(TIMEZONE).startOf("day");
  return [
    today.clone().subtract(DEFAULT_FETCH_DAYS, "days").format("YYYY-MM-DD"),
    today.clone().subtract(1, "day").format("YYYY-MM-DD"),
  ];
};

const buildDateRange = (startDate, endDate) => {
  const start = moment.tz(startDate, "YYYY-MM-DD", TIMEZONE);
  const end = moment.tz(endDate, "YYYY-MM-DD", TIMEZONE);

  if (!start.isValid() || !end.isValid()) {
    return [];
  }

  if (end.isBefore(start, "day")) {
    return [];
  }

  const dates = [];
  const cursor = start.clone();
  while (cursor.isSameOrBefore(end, "day")) {
    dates.push(cursor.format("YYYY-MM-DD"));
    cursor.add(1, "day");
  }

  return dates;
};

const buildArtifactBasePath = (dateStr) => {
  const [year, month, day] = String(dateStr || "").split("-");
  if (!year || !month || !day) {
    return "";
  }
  return `plot/map/bousai-championship/${year}/${month}/${day}`;
};

const buildArtifactUrl = (dateStr, fileName) => {
  const baseUrl = normalizeBaseUrl(PLOT_CLOUDFRONT_BASE_URL);
  const basePath = buildArtifactBasePath(dateStr);

  if (!baseUrl || !basePath || !fileName) {
    return "";
  }

  return `${baseUrl}/${basePath}/${fileName}`;
};

const fetchJson = async (url, label, signal) => {
  const response = await fetch(url, {
    method: "GET",
    mode: "cors",
    credentials: "omit",
    signal,
  });

  if (!response.ok) {
    throw new Error(`${label} fetch failed: ${response.status} ${response.statusText}`);
  }

  const text = await response.text();
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch (error) {
    throw new Error(`${label} parse failed: ${error.message}`);
  }
};

const buildThumbUrl = (objectKey) => {
  if (!objectKey || !S3_BUCKET_BASE_URL) {
    return "";
  }

  return `${S3_BUCKET_BASE_URL}/${objectKey}`;
};

const normalizePhoto = (photo, fallbackDate) => {
  if (!photo) {
    return null;
  }

  const fileName = String(photo.fileName || photo.file_name || "").trim();
  const objectKey = String(photo.objectKey || photo.object_key || "").trim();
  const id = String(photo.id || fileName || objectKey || "").trim();

  if (!id && !fileName && !objectKey) {
    return null;
  }

  return {
    id: id || `${fileName || objectKey || "photo"}:${fallbackDate || ""}`,
    fileName,
    objectKey,
    thumbUrl: buildThumbUrl(objectKey),
    label: String(photo.label || photo.photographerLabel || "撮影者"),
    photographerId: String(photo.photographerId || photo.photographer_id || ""),
    capturedAt: String(photo.capturedAt || photo.captured_at || fallbackDate || ""),
  };
};

const normalizePlotFeature = (feature, fallbackDate) => {
  const properties = feature?.properties || {};
  const coordinates = Array.isArray(feature?.geometry?.coordinates) ? feature.geometry.coordinates : [];
  const lng = Number(coordinates[0]);
  const lat = Number(coordinates[1]);
  const plotId = String(properties.plotId || properties.id || feature?.id || "").trim();
  const photographedAt = String(properties.photographedAt || properties.date || fallbackDate || "").trim();

  if (!plotId || !Number.isFinite(lng) || !Number.isFinite(lat)) {
    return null;
  }

  const photos = Array.isArray(properties.photos)
    ? properties.photos
        .map((photo) => normalizePhoto(photo, photographedAt))
        .filter(Boolean)
    : [];

  return {
    id: plotId,
    plotId,
    name: String(properties.name || `${properties.municipality || ""} ${properties.assetType || ""}`.trim() || "不明"),
    eventId: String(properties.eventId || ""),
    event: String(properties.event || "不明"),
    municipalityId: String(properties.municipalityId || ""),
    municipality: String(properties.municipality || "不明"),
    date: String(properties.date || photographedAt.slice(0, 10) || ""),
    photographedAt,
    lv: Number(properties.lv || 1),
    confidenceScore: Number(properties.confidenceScore || 0),
    assetType: String(properties.assetType || "不明"),
    assetTypeCode: String(properties.assetTypeCode || ""),
    assetSubType: String(properties.assetSubType || ""),
    lng,
    lat,
    photos,
    _sortKey: photographedAt ? new Date(photographedAt).getTime() || 0 : 0,
  };
};

const mergePhotos = (existingPhotos, incomingPhotos) => {
  const merged = [];
  const seen = new Set();

  [...(existingPhotos || []), ...(incomingPhotos || [])].forEach((photo) => {
    const key = photo?.id || `${photo?.fileName || ""}:${photo?.objectKey || ""}`;
    if (!key || seen.has(key)) {
      return;
    }
    seen.add(key);
    merged.push(photo);
  });

  return merged;
};

const mergePlot = (existing, incoming) => {
  if (!existing) {
    return incoming;
  }

  const keepIncoming = (incoming._sortKey || 0) >= (existing._sortKey || 0);
  const base = keepIncoming ? incoming : existing;
  const other = keepIncoming ? existing : incoming;

  return {
    ...base,
    photos: mergePhotos(existing.photos, incoming.photos),
    photoCount: mergePhotos(existing.photos, incoming.photos).length,
    _sortKey: Math.max(existing._sortKey || 0, incoming._sortKey || 0),
    name: base.name || other.name,
    eventId: base.eventId || other.eventId,
    event: base.event || other.event,
    municipalityId: base.municipalityId || other.municipalityId,
    municipality: base.municipality || other.municipality,
    assetType: base.assetType || other.assetType,
    assetTypeCode: base.assetTypeCode || other.assetTypeCode,
    assetSubType: base.assetSubType || other.assetSubType,
  };
};

const loadDailyArtifacts = async (dateStr, signal) => {
  if (dailyArtifactCache.has(dateStr)) {
    return dailyArtifactCache.get(dateStr);
  }

  const summaryUrl = buildArtifactUrl(dateStr, "summary.json");
  const geojsonUrl = buildArtifactUrl(dateStr, "plot.geojson.gz");

  if (!summaryUrl || !geojsonUrl) {
    throw new Error("PLOT_CLOUDFRONT_BASE_URL is not configured");
  }

  const artifactPromise = Promise.all([
    fetchJson(summaryUrl, `summary(${dateStr})`, signal),
    fetchJson(geojsonUrl, `geojson(${dateStr})`, signal),
  ]).then(([summary, geojson]) => ({
    dateStr,
    summary,
    geojson,
  }));

  dailyArtifactCache.set(dateStr, artifactPromise);

  try {
    return await artifactPromise;
  } catch (error) {
    if (dailyArtifactCache.get(dateStr) === artifactPromise) {
      dailyArtifactCache.delete(dateStr);
    }
    throw error;
  }
};

const mergeCountEntries = (summaries, key, identityKeys) => {
  const counts = new Map();

  summaries.forEach((summary) => {
    (summary?.[key] || []).forEach((item) => {
      const identity = identityKeys.map((identityKey) => item?.[identityKey] || "").join("|");
      const current = counts.get(identity) || { ...item, count: 0 };
      current.count += Number(item?.count || 0);
      counts.set(identity, current);
    });
  });

  return Array.from(counts.values()).sort((a, b) => b.count - a.count);
};

const mergeSummaries = (summaries) => {
  const merged = emptySummary();

  summaries.forEach((summary) => {
    merged.totals.plots += Number(summary?.totals?.plots || 0);
    merged.totals.photos += Number(summary?.totals?.photos || 0);
    merged.totals.scannedRows += Number(summary?.totals?.scannedRows || 0);
    merged.totals.skippedRows += Number(summary?.totals?.skippedRows || 0);
  });

  merged.trustCounts = mergeCountEntries(summaries, "trustCounts", ["lv"]);
  merged.eventCounts = mergeCountEntries(summaries, "eventCounts", ["eventId", "event"]);
  merged.municipalityCounts = mergeCountEntries(summaries, "municipalityCounts", ["municipalityId", "municipality"]);
  merged.assetTypeCounts = mergeCountEntries(summaries, "assetTypeCounts", ["assetType"]);

  return merged;
};

const loadPlotData = async (rangeTarget, signal) => {
  const [dateFrom, dateTo] = normalizeRange(rangeTarget);
  const targetDates = buildDateRange(dateFrom, dateTo);
  const plots = new Map();
  const summaries = [];

  for (const dateStr of targetDates) {
    const { summary, geojson } = await loadDailyArtifacts(dateStr, signal);
    if (summary) {
      summaries.push(summary);
    }
    const features = Array.isArray(geojson?.features) ? geojson.features : [];

    features.forEach((feature) => {
      const plot = normalizePlotFeature(feature, dateStr);
      if (!plot) {
        return;
      }

      const current = plots.get(plot.plotId);
      plots.set(plot.plotId, mergePlot(current, plot));
    });
  }

  const rows = Array.from(plots.values())
    .map((plot) => ({
      id: plot.id,
      plotId: plot.plotId,
      name: plot.name,
      eventId: plot.eventId,
      event: plot.event,
      municipalityId: plot.municipalityId,
      municipality: plot.municipality,
      date: plot.date,
      photographedAt: plot.photographedAt,
      lv: plot.lv,
      confidenceScore: plot.confidenceScore,
      assetType: plot.assetType,
      assetTypeCode: plot.assetTypeCode,
      assetSubType: plot.assetSubType,
      lng: plot.lng,
      lat: plot.lat,
      photos: plot.photos,
    }))
    .sort((a, b) => b.photographedAt.localeCompare(a.photographedAt) || String(a.id).localeCompare(String(b.id)));

  return {
    rows,
    summary: mergeSummaries(summaries),
  };
};

export function* handlePlotData(action) {
  yield put(setPlotLoading(true));
  yield put(setPlotError(""));
  const controller = new AbortController();

  try {
    const rangeTarget = action?.payload?.dateRange || (yield select((state) => state.app.dateRange));
    const { rows, summary } = yield call(loadPlotData, rangeTarget, controller.signal);

    // Keep the plot data shape compatible with the existing Plot page.
    yield put(setPlotData(rows));
    yield put(setPlotSummary(summary));

    if (PLOT_CLOUDFRONT_DISTRIBUTION_ID) {
      // eslint-disable-next-line no-console
      console.info(
        `[plotSaga] loaded from cloudfront distribution=${PLOT_CLOUDFRONT_DISTRIBUTION_ID} base=${PLOT_CLOUDFRONT_BASE_URL}`
      );
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("[plotSaga] failed to load plot data", error);
    yield put(setPlotData([]));
    yield put(setPlotSummary(emptySummary()));
    yield put(setPlotError("プロットデータ取得に失敗しました。"));
  } finally {
    if (yield cancelled()) {
      controller.abort();
    } else {
      yield put(setPlotLoading(false));
    }
  }
}

export default function* plotSaga() {
  yield takeLatest(PLOT.GET_PLOT_DATA, handlePlotData);
}
